import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import colorConfigs from "../../../configs/colorConfigs";
import { CircularProgress } from "@mui/material";
import AxiosMain from "../../../components/layout/AxiosMain";
import { AppAPiUrl } from "../../../configs/appData";

// Define the type for section labels
const sectionLabel = {
  customer: "Customer",
  deliveryAddress: "DeliveryAddress",
  invoiceAddress: "InvoiceAddress",
  commercialContact: "CommercialContact",
  technicalContact: "TechnicalContact",
} as const;

// Type representing the keys of sectionLabel
type SectionKey = keyof typeof sectionLabel;

interface Country {
  odoo_id: number;
  name: string;
}

interface Props {
  title: string;
  handleBack: () => void;
  handleNext: (data: any) => void;
  defaultData: any;
  iComponetTranslate: Array<{ label: string; value: string }>;
  countries: Country[];
  submitting: boolean;
}

const ReviewCustomerData = ({
  title,
  handleBack,
  handleNext,
  defaultData,
  iComponetTranslate,
  countries,
  submitting,
}: Props) => {

  if (!defaultData) {
    return <div>Loading data...</div>;
  }

  const t = (name: string): string => {
    const ifExist = iComponetTranslate.filter((item) => item.label === name);
    if (ifExist.length > 0) {
      return ifExist[0].value || `No translate found (${ifExist[0].label})`;
    }
    return `No key found (${name})`;
  };

  const isSectionKey = (key: string): key is SectionKey => {
    return key in sectionLabel;
  };

  const FetchCountryNameById: (id: number) => string | null = (id) => {
    const country = countries.filter((item) => item.odoo_id === id);
    if (country) {
      return country[0]?.name;
    }
    return "-";
  };

  const FetchStateById = (id: Number) => {
    const [stateName, setStateName] = useState("-");
    if (id) {
      AxiosMain.get(`${AppAPiUrl.FetchStateByID}${id}/`)
        .then((response) => {
          setStateName(response.data !== null ? response.data : "-");
        })
        .catch((error) => {
          console.error(error);
        });
    }
    return stateName;
  };

  const renderSection = (
    title: SectionKey,
    content: Record<string, string> | null
  ) => (
    <section key={title} style={styles.section}>
      <h3 style={styles.header}>{t(sectionLabel[title])}</h3>
      <div style={styles.gridContainer}>
        {content &&
          Object.entries(content).map(([key, value]) => (
            <div key={key} style={styles.gridItem}>
              <span style={styles.label}>{t(key)}:</span>
              {key === "country" && (
                <span style={styles.value}>
                  {FetchCountryNameById(parseInt(value, 10)) ?? "-"}
                </span>
              )}
              {key === "state" && (
                <span style={styles.value}>
                  {FetchStateById(parseInt(value))}
                </span>
              )}
              {key !== "country" && key !== "state" && (
                <span style={styles.value}>{value || "-"}</span>
              )}
            </div>
          ))}
      </div>
    </section>
  );

  return (
    <>
      <h1>{t(title)}</h1>
      <div style={styles.container}>
        {Object.entries(defaultData).map(([section, content]) => {
          if (!isSectionKey(section)) {
            console.warn(`Unknown section: ${section}`);
            return null;
          }
          return renderSection(
            section,
            content as unknown as Record<string, string>
          );
        })}
      </div>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Button
          onClick={handleBack}
          variant="contained"
          className="cm-btn-style"
          sx={{
            display: "flex",
            justifyContent: "end",
            color: colorConfigs.btnColor.colorDarkBlue,
            background: colorConfigs.btnStyle.color,
            "&:hover": {
              color: colorConfigs.btnColor.colorDarkBlue,
              background: colorConfigs.btnStyle.color,
            },
          }}
        >
          {t("Back")}
        </Button>
        {!submitting && <Button
          type="submit"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          className="cm-btn-style"
          onClick={handleNext}
          disabled={submitting}
          sx={{
            display: "flex",
            justifyContent: "end",
            color: colorConfigs.btnStyle.color,
            background: colorConfigs.btnColor.bgColorLightBlue,
            "&:hover": {
              color: colorConfigs.btnStyle.color,
              background: colorConfigs.btnColor.colorDarkBlue,
            },
          }}
        >
          {t("Submit")}
        </Button>}
        {submitting && <Button
          type="submit"
          variant="contained"
          endIcon={<CircularProgress size={'25px'} sx={{ color: "#00000" }} />}
          className="cm-btn-style"
          onClick={handleNext}
          disabled={true}
          sx={{
            display: "flex",
            justifyContent: "end",
            color: colorConfigs.btnStyle.color,
            background: colorConfigs.btnColor.bgColorLightBlue,
            "&:hover": {
              color: colorConfigs.btnStyle.color,
              background: colorConfigs.btnColor.colorDarkBlue,
            },
          }}
        >
              {t("submitting")}
        </Button>}
      </Box>
    </>
  );
};

// Define styles using CSS-in-JS approach
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "20px",
    maxWidth: "1000px",
    margin: "0 auto",
  },
  section: {
    marginBottom: "30px",
  },
  header: {
    textTransform: "capitalize",
    fontSize: "1.5rem",
    marginBottom: "15px",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontWeight: "bold",
    marginBottom: "5px",
  },
  value: {
    color: "#555",
  },
};

export default ReviewCustomerData;
